import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import axios from '../utils/axiosInstance';
import { BASE_URL } from '@/config/constants';

interface SectionConfigContextType {
  toursVisible: boolean;
  awardsVisible: boolean;
  loading: boolean;
  refreshConfig: () => void;
  setToursVisible: (visible: boolean) => void;
  setAwardsVisible: (visible: boolean) => void;
}

const SectionConfigContext = createContext<SectionConfigContextType>({
  toursVisible: true,
  awardsVisible: true,
  loading: false,
  refreshConfig: () => {},
  setToursVisible: () => {},
  setAwardsVisible: () => {},
});

export const useSectionConfig = () => useContext(SectionConfigContext);

interface SectionConfigProviderProps {
  children: ReactNode;
  selectedDomain: string;
}

/**
 * Provides section visibility config (tours & awards) to all child components.
 * Fetched ONCE per domain selection, shared between Sidebar, Dashboard, SiteBranding, etc.
 * Eliminates duplicate config API calls.
 */
export const SectionConfigProvider: React.FC<SectionConfigProviderProps> = ({ children, selectedDomain }) => {
  const [toursVisible, setToursVisible] = useState(true);
  const [awardsVisible, setAwardsVisible] = useState(true);
  const [loading, setLoading] = useState(false);

  const fetchConfig = useCallback(async () => {
    if (!selectedDomain) return;
    setLoading(true);
    try {
      const [toursRes, awardsRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/tours-events/section-config/get`),
        axios.get(`${BASE_URL}/api/awards-section/config/get`),
      ]);
      setToursVisible(toursRes.data.sectionActive !== false);
      setAwardsVisible(awardsRes.data.sectionActive !== false);
    } catch (err) {
      console.error('Error fetching section configs:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDomain]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Listen for real-time visibility changes from SiteBrandingManager
  useEffect(() => {
    const handleVisibilityChange = (event: CustomEvent<{ section: string; isVisible: boolean }>) => {
      const { section, isVisible } = event.detail;
      if (section === 'tours') {
        setToursVisible(isVisible);
      } else if (section === 'awards') {
        setAwardsVisible(isVisible);
      }
    };

    window.addEventListener('sectionVisibilityChanged', handleVisibilityChange as EventListener);
    return () => {
      window.removeEventListener('sectionVisibilityChanged', handleVisibilityChange as EventListener);
    };
  }, []);

  return (
    <SectionConfigContext.Provider value={{ toursVisible, awardsVisible, loading, refreshConfig: fetchConfig, setToursVisible, setAwardsVisible }}>
      {children}
    </SectionConfigContext.Provider>
  );
};
